import {
  CheckIcon,
  ClipboardCopyIcon,
  LinkIcon,
  MicrophoneIcon,
  MusicNoteIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import React from 'react';
import {
  FaGoogleDrive,
  FaInstagram,
  FaTiktok,
  FaYoutube,
} from 'react-icons/fa6';
import { toast } from 'sonner';
import { Audio } from 'types';

import clsxm from '@/lib/clsxm';
import { copyRichTextToClipboard } from '@/lib/copyTextToClipboard';

import {
  isLinkSource,
  isRecordingSource,
  TRANSCRIPTION_AUDIO_TYPES,
} from '@/constants/transcription';

/**
 * Shared look for AI Transcription (/audios and a transcript's page), in the
 * same vocabulary as the redesigned Agents dashboard (bots/DS/knowledgeUi):
 * white panels on the dashboard's grey, hairline borders, indigo only for
 * actions and state.
 */

/**
 * The pages' `t`. A key a locale hasn't translated yet falls back to the
 * English key with its {{placeholders}} filled in (next-translate otherwise
 * returns a missing key raw, placeholders and all).
 */
export const useTranscriptionT = () => {
  const { t, lang } = useLocale('transcription');
  const translate = React.useCallback(
    (key: string, query?: Record<string, unknown>) =>
      t(key, query, { default: key }),
    [t],
  );
  return { t: translate, lang: lang || 'en' };
};

export type TranscriptionT = ReturnType<typeof useTranscriptionT>['t'];

export const panel =
  'rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-white/10 dark:bg-slate-800/80 dark:shadow-none';

const focusRing =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900';

export const primaryButton = clsxm(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50',
  focusRing,
);

export const secondaryButton = clsxm(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white',
  focusRing,
);

export const quietButton = clsxm(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white',
  focusRing,
);

export const dangerButton = clsxm(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50',
  focusRing,
);

export const iconButton = clsxm(
  'inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white',
  focusRing,
);

export const inputClass =
  'block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900/60 dark:text-white dark:placeholder:text-slate-500';

export const labelClass =
  'block text-sm font-medium text-gray-800 dark:text-slate-200';

export const hintClass =
  'text-xs leading-relaxed text-gray-500 dark:text-slate-400';

/** Floating menu surface (row and header menus). */
export const menuPanel =
  'absolute right-0 z-30 mt-1.5 w-52 origin-top-right rounded-xl border border-gray-200/80 bg-white p-1 shadow-lg focus:outline-none dark:border-white/10 dark:bg-slate-800';

export const menuItem = (active: boolean, danger = false) =>
  clsxm(
    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm',
    danger
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-700 dark:text-slate-200',
    active &&
      (danger ? 'bg-red-50 dark:bg-red-500/10' : 'bg-gray-100 dark:bg-white/5'),
  );

/**
 * Copies text and confirms in place. `rich` pastes markdown as formatted
 * text (headings, lists) into Docs, Word or email.
 */
export const CopyButton = ({
  value,
  rich = false,
  label,
  className,
}: {
  value: string | null | undefined;
  rich?: boolean;
  label?: string;
  className?: string;
}) => {
  const { t } = useTranscriptionT();
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | undefined>(undefined);

  React.useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    const text = (value || '').trim();
    if (!text) return;
    try {
      if (rich) await copyRichTextToClipboard(text);
      else await navigator.clipboard.writeText(text);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error(t('Could not copy'));
    }
  };

  return (
    <button
      type='button'
      onClick={copy}
      disabled={!value?.trim()}
      className={clsxm(secondaryButton, 'px-2.5 py-1.5', className)}
    >
      {copied ? (
        <CheckIcon className='h-4 w-4 text-emerald-600' aria-hidden />
      ) : (
        <ClipboardCopyIcon className='h-4 w-4' aria-hidden />
      )}
      <span aria-live='polite'>
        {copied ? t('Copied') : label || t('Copy')}
      </span>
    </button>
  );
};

/* ------------------------------------------------------------------------ */

export type TranscriptionState =
  | 'ready'
  | 'processing'
  | 'failed'
  | 'no_speech'
  | 'draft';

type ListAudio = Pick<
  Audio,
  'transcription_status' | 'has_transcript' | 'type' | 'content'
> & { is_draft?: boolean };

/**
 * Where a transcription stands. A failed job never masquerades as
 * "Processing", and a silent recording never as a finished transcript.
 */
export const transcriptionState = (audio: ListAudio): TranscriptionState => {
  if (audio.has_transcript || audio.content) return 'ready';
  if (audio.transcription_status === 'failed') return 'failed';
  if (audio.transcription_status === 'done') return 'no_speech';
  if (audio.is_draft) return 'draft';
  return 'processing';
};

const STATE_STYLES: Record<TranscriptionState, { dot: string; pill: string }> =
  {
    ready: {
      dot: 'bg-emerald-500',
      // The normal state stays quiet so the exceptions stand out.
      pill: 'px-0 text-gray-600 dark:text-slate-300',
    },
    processing: {
      dot: 'bg-primary-500 motion-safe:animate-pulse',
      pill: 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-200',
    },
    failed: {
      dot: 'bg-red-500',
      pill: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
    },
    no_speech: {
      dot: 'bg-gray-400 dark:bg-slate-400',
      pill: 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-slate-300',
    },
    draft: {
      dot: 'bg-gray-300 dark:bg-slate-500',
      pill: 'text-gray-500 ring-1 ring-inset ring-gray-200 dark:text-slate-400 dark:ring-white/10',
    },
  };

export const stateLabel = (state: TranscriptionState, t: TranscriptionT) => {
  switch (state) {
    case 'ready':
      return t('Transcribed');
    case 'processing':
      return t('Processing');
    case 'failed':
      return t('Failed');
    case 'no_speech':
      return t('No speech');
    case 'draft':
      return t('Draft');
  }
};

export const StatePill = ({
  state,
  t,
  className,
}: {
  state: TranscriptionState;
  t: TranscriptionT;
  className?: string;
}) => {
  const style = STATE_STYLES[state];
  return (
    <span
      className={clsxm(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        style.pill,
        className,
      )}
    >
      <span
        aria-hidden
        className={clsxm('h-1.5 w-1.5 rounded-full', style.dot)}
      />
      {stateLabel(state, t)}
    </span>
  );
};

/* ------------------------------------------------------------------------ */

export type SourceKind =
  | 'youtube'
  | 'instagram'
  | 'tiktok'
  | 'googledrive'
  | 'link'
  | 'recording'
  | 'file';

export const sourceKind = (type: string | null | undefined): SourceKind => {
  if (isLinkSource(type)) return type === 'importurl' ? 'link' : type;
  if (isRecordingSource(type)) return 'recording';
  return 'file';
};

export const sourceLabel = (kind: SourceKind, t: TranscriptionT) => {
  switch (kind) {
    case 'youtube':
      return 'YouTube';
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'googledrive':
      return 'Google Drive';
    case 'link':
      return t('Link');
    case 'recording':
      // Not 'Recording': that key means "recording in progress" in every locale.
      return t('Audio recording');
    case 'file':
      return t('Uploaded file');
  }
};

const SOURCE_ICON: Record<
  SourceKind,
  { icon: React.ComponentType<{ className?: string }>; tone: string }
> = {
  youtube: { icon: FaYoutube, tone: 'text-[#ff0033]' },
  instagram: { icon: FaInstagram, tone: 'text-pink-600 dark:text-pink-400' },
  tiktok: { icon: FaTiktok, tone: 'text-gray-900 dark:text-white' },
  googledrive: {
    icon: FaGoogleDrive,
    tone: 'text-emerald-600 dark:text-emerald-400',
  },
  link: { icon: LinkIcon, tone: 'text-gray-500 dark:text-slate-300' },
  recording: {
    icon: MicrophoneIcon,
    tone: 'text-primary-600 dark:text-primary-300',
  },
  file: {
    icon: MusicNoteIcon,
    tone: 'text-primary-600 dark:text-primary-300',
  },
};

/** The source's icon on a small tile (list rows) or bare (meta lines). */
export const SourceIcon = ({
  kind,
  tile = true,
  className,
}: {
  kind: SourceKind;
  tile?: boolean;
  className?: string;
}) => {
  const { icon: Icon, tone } = SOURCE_ICON[kind];
  if (!tile) {
    return <Icon aria-hidden className={clsxm('h-4 w-4', tone, className)} />;
  }
  return (
    <span
      aria-hidden
      className={clsxm(
        'flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gray-50 ring-1 ring-inset ring-gray-200/70 dark:bg-white/5 dark:ring-white/10',
        className,
      )}
    >
      <Icon className={clsxm('h-[18px] w-[18px]', tone)} />
    </span>
  );
};

/* ------------------------------------------------------------------------ */

/** Title to show: drafts and API rows carry placeholder names. */
export const displayName = (
  audio: Pick<Audio, 'name' | 'audio_file_name'>,
  t: TranscriptionT,
) => {
  const name = (audio.name || '').trim();
  if (!name || name === 'New') return t('Untitled transcription');
  if (name.includes('New Audio ')) return audio.audio_file_name || name;
  return name;
};

/** The audio type in the reader's language, or null when it isn't one of ours. */
export const audioTypeLabel = (
  audioType: string | null | undefined,
  t: TranscriptionT,
) =>
  audioType &&
  (TRANSCRIPTION_AUDIO_TYPES as readonly string[]).includes(audioType)
    ? t(audioType)
    : null;

/**
 * audios.language holds a display name ("English") until the transcript
 * lands, then AssemblyAI's code ("en_us"). Codes read through Intl.
 */
export const languageLabel = (
  language: string | null | undefined,
  t: TranscriptionT,
  lang: string,
) => {
  const raw = (language || '').trim();
  if (!raw || raw.toLowerCase() === 'auto') return null;
  if (/^[a-z]{2,3}([_-][a-z]{2,4})?$/i.test(raw)) {
    try {
      const names = new Intl.DisplayNames([lang], { type: 'language' });
      const base = raw.split(/[_-]/)[0].toLowerCase();
      const label = names.of(base);
      if (label && label.toLowerCase() !== base) {
        return label.charAt(0).toLocaleUpperCase(lang) + label.slice(1);
      }
    } catch {
      return null;
    }
    return null;
  }
  return t(raw);
};

/* ------------------------------------------------------------------------ */

const relativeTime = (time: number, format: Intl.RelativeTimeFormat) => {
  const seconds = (time - Date.now()) / 1000;
  const size = Math.abs(seconds);
  const at = (value: number, unit: Intl.RelativeTimeFormatUnit) =>
    format.format(Math.round(value) || (seconds < 0 ? -1 : 1), unit);
  if (size < 45) return format.format(0, 'second');
  if (size < 45 * 60) return at(seconds / 60, 'minute');
  if (size < 22 * 3600) return at(seconds / 3600, 'hour');
  if (size < 26 * 86400) return at(seconds / 86400, 'day');
  if (size < 320 * 86400) return at(seconds / (30 * 86400), 'month');
  return at(seconds / (365 * 86400), 'year');
};

/** Timestamps from the database without a zone are UTC. */
const timeOf = (iso: string | null | undefined) => {
  if (!iso) return 0;
  const zoned = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const time = new Date(zoned).getTime();
  return Number.isFinite(time) ? time : 0;
};

/** "1:02:05" or "4:07" from seconds. */
export const formatClock = (seconds: number | string) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours
    ? `${hours}:${pad(minutes)}:${pad(rest)}`
    : `${minutes}:${pad(rest)}`;
};

/** Colours told apart at a glance, assigned to speakers in order of appearance. */
export const SPEAKER_COLORS = [
  'bg-primary-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-sky-500',
  'bg-rose-500',
  'bg-violet-500',
  'bg-teal-500',
  'bg-orange-500',
];

export type TranscriptionFormatters = ReturnType<
  typeof useTranscriptionFormatters
>;

export const useTranscriptionFormatters = () => {
  const { lang } = useTranscriptionT();
  return React.useMemo(() => {
    const number = new Intl.NumberFormat(lang);
    const relative = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    const dateTime = new Intl.DateTimeFormat(lang, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    return {
      number: (value: number) => number.format(Math.max(0, Math.round(value))),
      /** "3 days ago"; '' when unknown. */
      fromNow: (iso: string | null | undefined) => {
        const time = timeOf(iso);
        return time ? relativeTime(time, relative) : '';
      },
      /** "Oct 7, 2026, 3:12 PM" in the reader's language. */
      dateTime: (iso: string | null | undefined) => {
        const time = timeOf(iso);
        return time ? dateTime.format(new Date(time)) : '';
      },
      clock: formatClock,
    };
  }, [lang]);
};
